/*
 * Copyright © 2026. Legalbird eine Marke der Legalbird GmbH
 */

import { Box, Container, Divider, Stack, Typography } from "@mui/material";
import { ReactNode, useRef, useState } from "react";
import { useScroll, useMotionValueEvent } from "motion/react";
import { mergeSx } from "@/src/utils/mergeSx";
import { styleguideSectionStyle } from "./styleguideSectionStyle";
import { useStyleguideContext } from "@/src/styleguide/components/Styleguide/StyleguideProvider.tsx";

export type StyleguideSectionProps = {
  children?: ReactNode;
  id: string;
  title: string;
  level: 1 | 2;
  fullWidth?: boolean;
};

export default function StyleguideSection({ children, id, title, level, fullWidth }: StyleguideSectionProps) {
  const { activeSection, setActiveSection } = useStyleguideContext();
  const headerHeightRef = useRef<number>(0);
  const sectionRef = useRef<HTMLDivElement>(null);
  const { scrollY } = useScroll();

  useMotionValueEvent(scrollY, "change", () => {
    if (level === 1) {
      return;
    }

    const sectionRect = sectionRef.current?.getBoundingClientRect();
    if (sectionRect && sectionRect?.top <= 0 && sectionRect?.bottom >= 0) {
      setActiveSection({ title, level });
      return;
    } else if (activeSection?.title === title) {
      setActiveSection(null);
    }
  });

  return (
    <Stack id={id} spacing={"2rem"} sx={{ width: "100%", alignItems: "center" }} ref={sectionRef}>
      <Box sx={{ width: "100%" }}>
        <Box
          ref={(ele: HTMLElement) => {
            headerHeightRef.current = ele?.scrollHeight ?? 0;
          }}
          sx={mergeSx(styleguideSectionStyle.header)}
        >
          <Stack component={fullWidth || level === 1 ? Container : "div"}>
            <Typography variant={level === 1 ? "h2" : "h3"} sx={styleguideSectionStyle.title}>
              {level === 1 ? <strong>{title}</strong> : title}
            </Typography>
            <Divider sx={{ width: "100%" }} />
          </Stack>
        </Box>
      </Box>
      <Stack component={!fullWidth ? Container : "div"} disableGutters={level === 2} spacing={"1rem"} data-level={level} sx={{ width: "100%" }}>
        {children}
      </Stack>
    </Stack>
  );
}
